/**
 * 资源匹配锚点组件
 * 连接用户工具箱与养护方案
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5
 */

import React from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  CheckCircle,
  XCircle,
  Sparkles,
  Package,
  BadgePercent,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { InventoryAnchorProps } from "@/types/conversion-funnel";

/**
 * 准备度分数环形指示器
 */
interface ScoreRingProps {
  score: number;
  size?: number;
}

function ScoreRing({ score, size = 48 }: ScoreRingProps) {
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - score / 100);

  // 根据分数确定颜色
  const getColor = () => {
    if (score >= 80) return "hsl(var(--chart-2))"; // green
    if (score >= 50) return "hsl(var(--chart-4))"; // amber
    return "hsl(var(--destructive))"; // red
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* 背景圆 */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeWidth}
        />
        {/* 进度圆 */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-500 ease-out"
        />
      </svg>
      {/* 分数文字 */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold text-foreground">{score}%</span>
      </div>
    </div>
  );
}

/**
 * 资源匹配锚点
 * 显示准备度分数、已有物品、节省费用和缺失物品
 */
export function InventoryAnchor({
  inventoryMatch,
  className,
}: InventoryAnchorProps) {
  const { t } = useTranslation("assessments");
  const { score, saved_money_text, has_items, missing_items } = inventoryMatch;

  // 根据分数确定鼓励文案
  const getEncouragementText = () => {
    if (score >= 80) {
      return t(
        "conversion.inventory.highScore",
        "无需购买昂贵药剂，使用现有物资即可开始养护"
      );
    }
    if (score >= 50) {
      return t(
        "conversion.inventory.mediumScore",
        "您已准备好大部分物资，仅需补充少量用品"
      );
    }
    return t("conversion.inventory.lowScore", "建议先准备必要的护理用品");
  };

  return (
    <Card
      className={cn(
        "p-3 sm:p-4 bg-gradient-to-r from-emerald-50/50 to-blue-50/50 dark:from-emerald-950/20 dark:to-blue-950/20",
        className
      )}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        {/* 准备度分数 */}
        <div className="flex-shrink-0">
          <ScoreRing score={score} />
        </div>

        {/* 内容区 */}
        <div className="flex-1 min-w-0">
          {/* 标题和鼓励文案 */}
          <div className="flex items-center gap-2 mb-1">
            <Package className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">
              {t("conversion.inventory.title", "物资准备度")}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            {getEncouragementText()}
          </p>

          {/* 已有物品 */}
          {has_items.length > 0 && (
            <div className="mb-2">
              <div className="flex flex-wrap gap-2">
                {has_items.map((item, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                    className="bg-emerald-100/50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800"
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* 缺失物品 */}
          {missing_items.length > 0 && (
            <div className="mb-2">
              <div className="flex flex-wrap gap-2">
                {missing_items.map((item, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                    className="bg-amber-100/50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800"
                  >
                    <XCircle className="h-3 w-3 mr-1" />
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* 节省费用提示 */}
          {saved_money_text && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
              <BadgePercent className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
              <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                {saved_money_text}
              </span>
              <Sparkles className="h-4 w-4 text-amber-400" />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default InventoryAnchor;
